/**
 * Componente: PlanBillingSheet
 * Descrição: Sheet lateral com histórico de pagamentos do plano
 * 
 * Exibe:
 * - Timeline de pagamentos do plano base
 * - Links para PDF e visualização no Stripe
 * - Status de cada pagamento
 * 
 * EXCLUSIVO PARA USO NO PAINEL ADMIN
 * 
 * @author Sistema
 * @created 2026-01-19
 */

import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import {
  Crown,
  Sparkles,
  Zap,
  Package,
  FileText,
  ExternalLink,
  CheckCircle,
  Clock,
  Receipt,
  AlertCircle,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { translateStripeDescription } from "@/lib/stripe-description-translator";

// ============================================================================
// INTERFACES
// ============================================================================

/**
 * Interface de registro de histórico de faturamento
 */
interface BillingHistoryRecord {
  id: string;
  stripe_invoice_id: string | null;
  amount_paid: number;
  currency: string;
  status: string;
  invoice_url: string | null;
  invoice_pdf: string | null;
  plan_name: string | null;
  billing_period: string | null;
  period_start: string | null;
  period_end: string | null;
  created_at: string;
  line_items?: unknown[] | null;
}

interface PlanBillingSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  planName: string;
  planId: string;
  billingHistory?: BillingHistoryRecord[];
}

// ============================================================================
// FUNÇÕES AUXILIARES
// ============================================================================

/**
 * Formatar valor em centavos para moeda BRL
 */
const formatCurrency = (valueInCents: number, currency: string = "brl") => {
  const value = valueInCents / 100;
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(value);
};

/**
 * Traduzir status para português
 */
const translateStatus = (status: string) => {
  const translations: Record<string, string> = {
    paid: "Pago",
    open: "Em Aberto",
    pending: "Pendente",
    failed: "Falhou",
    uncollectible: "Incobrável",
    void: "Cancelada",
    draft: "Rascunho",
  };
  return translations[status] || status;
};

/**
 * Obter cor do status
 */
const getStatusColor = (status: string) => {
  switch (status) {
    case "paid":
      return "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400";
    case "open":
    case "pending":
      return "bg-amber-500/10 text-amber-600 dark:text-amber-400";
    case "failed":
    case "uncollectible":
      return "bg-red-500/10 text-red-600 dark:text-red-400";
    default:
      return "bg-muted text-muted-foreground";
  }
};

/**
 * Obter ícone do plano
 */
const getPlanIcon = (planId: string) => {
  switch (planId) {
    case "enterprise": return Crown;
    case "professional": return Sparkles;
    case "starter": return Zap;
    default: return Package;
  }
};

/**
 * Obter cor do plano
 */
const getPlanColor = (planId: string) => {
  switch (planId) {
    case "enterprise": return "text-amber-600 dark:text-amber-400 bg-amber-500/10";
    case "professional": return "text-purple-600 dark:text-purple-400 bg-purple-500/10";
    case "starter": return "text-blue-600 dark:text-blue-400 bg-blue-500/10";
    default: return "text-emerald-600 dark:text-emerald-400 bg-emerald-500/10";
  }
};

// ============================================================================
// COMPONENTE: CARD DE HISTÓRICO DE FATURAMENTO
// ============================================================================

interface BillingHistoryCardProps {
  record: BillingHistoryRecord;
}

/**
 * Card individual de registro de pagamento do plano
 */
interface PlanBillingHistoryCardExtendedProps extends BillingHistoryCardProps {
  planPriceIds: Set<string>;
}

function PlanBillingHistoryCard({ record, planPriceIds }: PlanBillingHistoryCardExtendedProps) {
  // Obter descrição dos itens do plano
  const lineItems = record.line_items as Array<{
    description?: string;
    price_id?: string;
    amount?: number;
    quantity?: number;
  }> | null;
  
  // Encontrar itens do plano base usando price_id
  const planItems = lineItems?.filter((item) => {
    // Verificar pelo price_id primeiro
    if (item.price_id && planPriceIds.has(item.price_id)) {
      return true;
    }
    return false;
  }) || [];

  // Calcular valor total do plano (sem add-ons)
  const planAmount = planItems.reduce((sum, item) => sum + (item.amount || 0), 0);
  // Se não encontrou itens específicos, usar o valor total do registro
  const displayAmount = planAmount > 0 ? planAmount : record.amount_paid;

  // Descrição do período
  const periodDescription = record.period_start && record.period_end 
    ? `${format(new Date(record.period_start), "dd/MM/yyyy")} - ${format(new Date(record.period_end), "dd/MM/yyyy")}`
    : null;

  return (
    <Card>
      <CardContent className="py-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {/* Ícone de status */}
            <div className={cn(
              "h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0",
              record.status === "paid" 
                ? "bg-emerald-500/10" 
                : record.status === "failed"
                  ? "bg-red-500/10"
                  : "bg-amber-500/10"
            )}>
              {record.status === "paid" ? (
                <CheckCircle className="h-5 w-5 text-emerald-500" />
              ) : record.status === "failed" ? (
                <AlertCircle className="h-5 w-5 text-red-500" />
              ) : (
                <Clock className="h-5 w-5 text-amber-500" />
              )}
            </div>
            
            {/* Valor e data */}
            <div>
              <p className="font-semibold">
                {formatCurrency(displayAmount, record.currency)}
              </p>
              <p className="text-xs text-muted-foreground">
                {format(new Date(record.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
              </p>
              {periodDescription && (
                <p className="text-xs text-muted-foreground mt-0.5">
                  Período: {periodDescription}
                </p>
              )}
            </div>
          </div>
          
          {/* Status e ações */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <Badge 
              variant="secondary" 
              className={cn(getStatusColor(record.status))}
            >
              {translateStatus(record.status)}
            </Badge>
            
            {/* Botão PDF */}
            {record.invoice_pdf && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => window.open(record.invoice_pdf!, "_blank")}
                title="Baixar PDF"
              >
                <FileText className="h-4 w-4" />
              </Button>
            )}
            
            {/* Botão ver fatura no Stripe */}
            {record.invoice_url && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => window.open(record.invoice_url!, "_blank")}
                title="Ver no Stripe"
              >
                <ExternalLink className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export function PlanBillingSheet({
  open,
  onOpenChange,
  planName,
  planId,
  billingHistory = [],
}: PlanBillingSheetProps) {
  // Obter ícone e cor do plano
  const PlanIcon = getPlanIcon(planId);
  const planColor = getPlanColor(planId);

  // Buscar price_ids dos planos cadastrados para filtragem correta
  const { data: planPriceIds } = useQuery({
    queryKey: ["plan-price-ids-for-billing"],
    queryFn: async () => {
      const { data } = await supabase
        .from("subscription_plans")
        .select("stripe_price_id_monthly, stripe_price_id_yearly")
        .not("stripe_price_id_monthly", "is", null);
      
      // Criar Set de todos os price_ids de planos
      const priceIds = new Set<string>();
      data?.forEach((plan) => {
        if (plan.stripe_price_id_monthly) priceIds.add(plan.stripe_price_id_monthly);
        if (plan.stripe_price_id_yearly) priceIds.add(plan.stripe_price_id_yearly);
      });
      return priceIds;
    },
    staleTime: 1000 * 60 * 30, // Cache por 30 minutos
    enabled: open, // Só buscar quando o sheet estiver aberto
  });

  // Filtrar histórico para mostrar apenas pagamentos que contêm itens de PLANO
  const planBillingHistory = useMemo(() => {
    // Se ainda não temos os price_ids, retornar vazio
    if (!planPriceIds || planPriceIds.size === 0) return [];
    
    return billingHistory.filter((record) => {
      const lineItems = record.line_items as Array<{
        description?: string;
        price_id?: string;
      }> | null;
      
      // Se não há line_items, verificar pelo plan_name (registros antigos)
      if (!lineItems?.length) {
        if (record.plan_name) {
          const lowerName = record.plan_name.toLowerCase();
          return lowerName.includes('enterprise') || 
                 lowerName.includes('professional') ||
                 lowerName.includes('starter');
        }
        return false;
      }
      
      // Verificar se ALGUM item tem price_id que pertence a um plano
      return lineItems.some((item) => {
        return item.price_id && planPriceIds.has(item.price_id);
      });
    });
  }, [billingHistory, planPriceIds]);

  // Calcular totais
  const totalPaid = planBillingHistory
    .filter(r => r.status === 'paid')
    .reduce((sum, r) => sum + r.amount_paid, 0);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-lg w-full">
        <SheetHeader className="pb-4">
          <SheetTitle className="flex items-center gap-2">
            <div className={cn(
              "flex h-8 w-8 items-center justify-center rounded-lg",
              planColor
            )}>
              <PlanIcon className="h-4 w-4" />
            </div>
            {planName} - Faturamento
          </SheetTitle>
          <SheetDescription>
            Histórico de pagamentos do plano
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-180px)] pr-4">
          <div className="space-y-4">
            {/* Card de resumo */}
            <Card className="bg-muted/50">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Receipt className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Total pago</span>
                  </div>
                  <span className="font-bold text-primary">
                    {formatCurrency(totalPaid)}
                  </span>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-sm text-muted-foreground">Pagamentos registrados</span>
                  <span className="font-medium">{planBillingHistory.length}</span>
                </div>
              </CardContent>
            </Card>

            {/* Lista de pagamentos */}
            {planBillingHistory.length > 0 ? (
              <div className="space-y-3">
                {planBillingHistory.map((record) => (
                  <PlanBillingHistoryCard 
                    key={record.id} 
                    record={record}
                    planPriceIds={planPriceIds || new Set()}
                  />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-4">
                  <Receipt className="h-6 w-6 text-muted-foreground" />
                </div>
                <p className="text-sm font-medium">Nenhum pagamento encontrado</p>
                <p className="text-xs text-muted-foreground mt-1">
                  O histórico de pagamentos aparecerá aqui após a primeira cobrança.
                </p>
              </div>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
